// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import cx from 'classnames'
import moment from 'moment'
import React from 'react'
import { get } from 'react-hook-form'
import Loader from '../../../common/loader/Loader'
import { Query } from '../../../react-apollo/Components'
import { RestrictedConsumer } from '../../auth/RestrictedContext'
import { i18n } from '../../index'
import RequiresAction from '../RequiresAction'
import css from '../weeklyInfoPage.css'
import { fetchWeeklyInfo } from '../weeklyInfoQueries'
import { WeeklyInfoGeneral, WeeklyInfoVariables } from '../weeklyInfoTypes'

interface Props {
  date: string
  stationId: string
}

interface SalesAndHoursRowProps {
  stationId: string
  date: string
  data: WeeklyInfoGeneral
  canEdit: boolean
}

export default function MarketSalesAndHours({ date, stationId }: Props) {
  return (
    <Query<Array<WeeklyInfoGeneral>, WeeklyInfoVariables>
      query={fetchWeeklyInfo}
      variables={{
        stationId,
        date,
        type: 'General',
      }}
    >
      {({ data, error, loading }) => (
        <>
          <Loader
            {...{
              loading,
              error,
            }}
          >
            <div className={css.salesAndHours}>
              <Typography variant='h6' gutterBottom>
                {i18n.t('weeklyInfo.title.salesAndHours', {
                  week: `vko. ${moment(date).isoWeek()} ${moment(date).startOf('week').format('D.M.')}-${moment(date)
                    .endOf('week')
                    .format('D.M.')}`,
                })}
              </Typography>
              <RestrictedConsumer>
                {(hasPermission) => (
                  <Paper>
                    <Table size='small' className={css.table}>
                      <TableHead>
                        <TableRow>
                          <TableCell align='center' className={css.tableHeading} />
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t('weeklyInfo.heading.budgetedSalesTaxfree')}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t('weeklyInfo.heading.actualSalesTaxfree')}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t('weeklyInfo.heading.plannedSalesInclTax')}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t('weeklyInfo.heading.actualHours')}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t('weeklyInfo.heading.plannedHours')}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t('weeklyInfo.heading.actualEfficiencyTaxfree')}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t('weeklyInfo.heading.plannedEfficiencyInclTax')}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t('weeklyInfo.heading.budgetedEfficiencyTaxfree')}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.actionCell)}>
                            {hasPermission && i18n.t('weeklyInfo.heading.isActionRequired')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {get(data, 'weeklyInfo', []).map(
                          (item) =>
                            item.profitCenterType === 'Market' && (
                              <SalesAndHoursRow
                                stationId={stationId}
                                date={date}
                                data={item}
                                key={item.id}
                                canEdit={hasPermission}
                              />
                            ),
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                )}
              </RestrictedConsumer>
            </div>
          </Loader>
        </>
      )}
    </Query>
  )
}

const calculateEfficiency = (actualSales: number, actualHours: number): number =>
  actualSales && actualHours ? Math.round(actualSales / actualHours) : 0

function SalesAndHoursRow({ stationId, date, data, canEdit }: SalesAndHoursRowProps) {
  const actualSalesTaxfree = Math.round(data.totalActualSalesTaxfree) || 0
  const plannedSales = Math.round(data.totalPlannedSales) || 0
  const budgetedSalesTaxfree = Math.round(data.totalBudgetedSalesTaxfree) || 0
  const actualHours = Math.round(data.totalActualHours) || 0
  const plannedHours = Math.round(data.totalPlannedHours) || 0
  const actualEfficiencyTaxfree = calculateEfficiency(actualSalesTaxfree, actualHours)
  const budgetedEfficiencyTaxfree = Math.round(data.budgetedEfficiencyTaxfree) || 0
  const plannedEfficiency = Math.round(data.plannedEfficiency) || 0
  return (
    <TableRow>
      <TableCell align='center' className={cx(css.tableCell, css.headingCell)}>
        {i18n.t(`weeklyInfo.heading.profitCenter${data.profitCenterType}`)}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        <strong>{budgetedSalesTaxfree} &euro;</strong>
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: actualSalesTaxfree >= plannedSales,
          [css.negativeCell]: actualSalesTaxfree < plannedSales,
        })}
      >
        {actualSalesTaxfree} &euro;
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {plannedSales} &euro;
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: actualHours <= plannedHours,
          [css.negativeCell]: actualHours > plannedHours,
        })}
      >
        {actualHours} h
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {plannedHours} h
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: actualEfficiencyTaxfree >= plannedEfficiency,
          [css.negativeCell]: actualEfficiencyTaxfree < plannedEfficiency,
        })}
      >
        {actualEfficiencyTaxfree}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {plannedEfficiency}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        <strong>{budgetedEfficiencyTaxfree}</strong>
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.actionCell)}>
        {canEdit && (
          <RequiresAction
            stationId={stationId}
            date={date}
            weeklyInfoId={data.id}
            subject='Management'
            isActionRequired={data.isActionRequired}
          />
        )}
      </TableCell>
    </TableRow>
  )
}
